import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faInfoCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import { ToastFailureMessage, ToastPayload, ToastSuccessMessage } from '../../Models/Models';

/* ── Style map ────────────────────────────────────────────────────────── */

interface ToastStyle {
    accentColor: string;
    bgColor: string;
    icon: any;
}

const SUCCESS_STYLE: ToastStyle = { accentColor: 'var(--st-success)', bgColor: 'var(--st-success-soft)', icon: faCheckCircle };
const ERROR_STYLE: ToastStyle = { accentColor: 'var(--st-danger)', bgColor: 'var(--st-danger-soft)', icon: faTimesCircle };
const INFO_STYLE: ToastStyle = { accentColor: 'var(--st-accent)', bgColor: 'var(--st-accent-soft)', icon: faInfoCircle };

function resolveStyle(message: ToastSuccessMessage | ToastFailureMessage): ToastStyle {
    if (Object.values(ToastFailureMessage).includes(message as ToastFailureMessage)) return ERROR_STYLE;
    if (Object.values(ToastSuccessMessage).includes(message as ToastSuccessMessage)) return SUCCESS_STYLE;
    return INFO_STYLE;
}

const AUTOHIDE_MS = 5000;
const EXIT_MS = 280;

/* ── Component ────────────────────────────────────────────────────────── */

export default function ToastComponent({ toast, onClose }: {
    toast: ToastPayload | null;
    onClose: () => void;
}) {
    const [visible, setVisible] = useState(false);
    const [displayed, setDisplayed] = useState<ToastPayload | null>(null);

    useEffect(() => {
        if (!toast) return;

        setDisplayed(toast);
        setVisible(true);

        const autohide = setTimeout(() => {
            setVisible(false);
            setTimeout(onClose, EXIT_MS);
        }, AUTOHIDE_MS);

        return () => clearTimeout(autohide);
    }, [toast]);

    if (!displayed) return null;

    const { accentColor, bgColor, icon } = resolveStyle(displayed.message);

    const handleClose = () => {
        setVisible(false);
        setTimeout(onClose, EXIT_MS);
    };

    return (
        <div
            role="alert"
            aria-live="polite"
            style={{
                position: 'fixed',
                bottom: '1.5rem',
                right: '1.5rem',
                zIndex: 1_000_000,
                display: 'flex',
                alignItems: 'stretch',
                backgroundColor: 'var(--st-paper)',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(23,20,31,0.14), 0 1px 4px rgba(23,20,31,0.08)',
                overflow: 'hidden',
                minWidth: '17rem',
                maxWidth: '24rem',
                transform: visible ? 'translateY(0)' : 'translateY(calc(100% + 2rem))',
                opacity: visible ? 1 : 0,
                transition: `transform ${EXIT_MS}ms cubic-bezier(0.34,1.4,0.64,1), opacity ${EXIT_MS}ms ease`,
                pointerEvents: visible ? 'auto' : 'none',
            }}
        >
            {/* Left accent stripe */}
            <div style={{ width: '4px', backgroundColor: accentColor, flexShrink: 0 }} />

            {/* Icon panel */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0 0.85rem',
                backgroundColor: bgColor,
                flexShrink: 0,
            }}>
                <FontAwesomeIcon icon={icon} style={{ color: accentColor, fontSize: '1.15rem' }} />
            </div>

            {/* Message */}
            <p style={{
                flex: 1,
                margin: 0,
                padding: '0.9rem 0.75rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'var(--st-text)',
                lineHeight: 1.45,
            }}>
                {displayed.message}
            </p>

            {/* Close button */}
            <button
                onClick={handleClose}
                aria-label="Zavrieť"
                style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.75rem 0.75rem 0',
                    color: 'var(--st-text-faint)',
                    fontSize: '0.75rem',
                    alignSelf: 'flex-start',
                    flexShrink: 0,
                    lineHeight: 1,
                    transition: 'color 0.15s',
                }}
                onMouseEnter={e => (e.currentTarget.style.color = 'var(--st-text-soft)')}
                onMouseLeave={e => (e.currentTarget.style.color = 'var(--st-text-faint)')}
            >
                <FontAwesomeIcon icon={faTimes} />
            </button>
        </div>
    );
}
